/**
 * Utilitário para validação e formatação de CPF
 * Implementa o algoritmo oficial de validação de CPF da Receita Federal
 */

/**
 * Valida um CPF usando o algoritmo oficial
 * @param {string} cpf - CPF a ser validado (pode conter formatação)
 * @returns {boolean} - true se o CPF for válido, false caso contrário
 */
export const validateCPF = (cpf) => {
  // Remove caracteres não numéricos
  cpf = cpf.replace(/\D/g, '');
  
  // Verifica se tem 11 dígitos
  if (cpf.length !== 11) return false;
  
  // Verifica se todos os dígitos são iguais (CPFs inválidos conhecidos)
  if (/^(\d)\1{10}$/.test(cpf)) return false;
  
  // Validação do primeiro dígito verificador
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cpf.charAt(i)) * (10 - i);
  }
  let remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cpf.charAt(9))) return false;
  
  // Validação do segundo dígito verificador
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cpf.charAt(i)) * (11 - i);
  }
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cpf.charAt(10))) return false;
  
  return true;
};

/**
 * Formata um CPF no padrão XXX.XXX.XXX-XX
 * @param {string} value - Valor a ser formatado
 * @returns {string} - CPF formatado
 */
export const formatCPF = (value) => {
  const numbers = value.replace(/\D/g, '');
  
  if (numbers.length <= 3) return numbers;
  if (numbers.length <= 6) return `${numbers.slice(0, 3)}.${numbers.slice(3)}`;
  if (numbers.length <= 9) return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6)}`;
  return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6, 9)}-${numbers.slice(9, 11)}`;
};

/**
 * Remove a formatação de um CPF, deixando apenas os números
 * @param {string} cpf - CPF formatado
 * @returns {string} - CPF apenas com números
 */
export const unformatCPF = (cpf) => {
  return cpf.replace(/\D/g, '');
};

/**
 * Verifica se um CPF está no formato correto (XXX.XXX.XXX-XX)
 * @param {string} cpf - CPF a ser verificado
 * @returns {boolean} - true se estiver no formato correto
 */
export const isCPFFormatted = (cpf) => {
  return /^\d{3}\.\d{3}\.\d{3}-\d{2}$/.test(cpf);
};

/**
 * Gera um CPF válido para testes (não usar em produção)
 * @returns {string} - CPF válido formatado
 */
export const generateValidCPF = () => {
  // Gera 9 dígitos aleatórios
  let cpf = '';
  for (let i = 0; i < 9; i++) {
    cpf += Math.floor(Math.random() * 10);
  }
  
  // Calcula o primeiro dígito verificador
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cpf.charAt(i)) * (10 - i);
  }
  let remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  cpf += remainder;
  
  // Calcula o segundo dígito verificador
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cpf.charAt(i)) * (11 - i);
  }
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  cpf += remainder;
  
  return formatCPF(cpf);
};

// Exemplos de uso:
// const cpf = "123.456.789-09";
// const isValid = validateCPF(cpf); // true
// const formatted = formatCPF("12345678909"); // "123.456.789-09"
// const unformatted = unformatCPF("123.456.789-09"); // "12345678909"
